/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import {InvoiceUploadStats} from '../../../util/interfaces';
import {AppContext} from '../../misc/appContext';
import {ResponseData} from '@dvrd/dvr-controls';
import {createErrorMessage} from '../../../util/utils';
import Account from '../../../models/accountModel';
import UploadStats from './uploadStats';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    stats: InvoiceUploadStats;
    loading: boolean;
}

export default function UploadStatsController(props: Props) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const {stats} = props;
    const [loading, setLoading] = useState(props.loading);
    const proactiveEnabled = account?.settings.proactivelyUploadInvoices ?? false;
    const autoRespondEnabled = account?.settings.autoRespondInvoiceRequests ?? false;

    function onChangeProactiveUpload(value: boolean) {
        if (!account) return;
        setLoading(true);
        account.settings.proactivelyUploadInvoices = value;
        updateAccount(account, (data: ResponseData) => {
            if (data.success) {
                const label = value
                    ? t('dashboard.uploadStats.enabled', 'ingeschakeld')
                    : t('dashboard.uploadStats.disabled', 'uitgeschakeld');
                showSnackbar(
                    `${t('dashboard.uploadStats.proactiveUploadPrefix', 'Het proactief uploaden van de facturen is')} ${label}`
                );
            } else {
                account.settings.proactivelyUploadInvoices = !value;
                if (data.handled) return;
                const label = value
                    ? t('dashboard.uploadStats.enable', 'inschakelen')
                    : t('dashboard.uploadStats.disable', 'uitschakelen');
                showDialog(
                    createErrorMessage(
                        data.message ??
                            `${t('dashboard.uploadStats.proactiveFailedPrefix', 'Het')} ${label} ${t('dashboard.uploadStats.proactiveFailedSuffix', 'van het proactief uploaden is niet gelukt.')}`
                    ),
                    `${t('dashboard.uploadStats.proactiveFailedTitlePrefix', 'Proactief uploaden')} ${label} ${t('dashboard.uploadStats.failedSuffix', 'mislukt')}`
                );
            }
        });
    }

    function onChangeAutoRespond(value: boolean) {
        if (!account) return;
        setLoading(true);
        account.settings.autoRespondInvoiceRequests = value;
        updateAccount(account, (data: ResponseData) => {
            if (data.success) {
                const label = value
                    ? t('dashboard.uploadStats.enabled', 'ingeschakeld')
                    : t('dashboard.uploadStats.disabled', 'uitgeschakeld');
                showSnackbar(
                    `${t('dashboard.uploadStats.autoRespondPrefix', 'Het automatisch beantwoorden van de aanvragen is')} ${label}`
                );
            } else {
                account.settings.autoRespondInvoiceRequests = !value;
                if (data.handled) return;
                const label = value
                    ? t('dashboard.uploadStats.enable', 'inschakelen')
                    : t('dashboard.uploadStats.disable', 'uitschakelen');
                showDialog(
                    createErrorMessage(
                        data.message ??
                            `${t('dashboard.uploadStats.autoRespondFailedPrefix', 'Het')} ${label} ${t('dashboard.uploadStats.autoRespondFailedSuffix', 'van het automatisch beantwoorden is niet gelukt.')}`
                    ),
                    `${t('dashboard.uploadStats.autoRespondFailedTitlePrefix', 'Automatisch beantwoorden')} ${label} ${t('dashboard.uploadStats.failedSuffix', 'mislukt')}`
                );
            }
        });
    }

    function updateAccount(account: Account, callback: (data: ResponseData) => void) {
        setLoading(true);
        account.update(undefined, undefined, undefined, (account: Account, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) context.onChangeContext({account});
            callback(data);
        });
    }

    useEffect(() => {
        setLoading(props.loading);
    }, [props.loading]);

    return (
        <UploadStats
            onChangeProactiveUpload={onChangeProactiveUpload}
            onChangeAutoRespond={onChangeAutoRespond}
            proactiveEnabled={proactiveEnabled}
            stats={stats}
            loading={loading}
            autoRespondEnabled={autoRespondEnabled}
        />
    );
}
